import { TEMPLATE_PERMISSIONS } from '@hubertai/screening-question-types';
import clsx from 'clsx';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import AccessGroupCombobox from 'components/comboboxes/AccessGroupCombobox';
import { useUser } from 'hooks/useUser';
import { useGetClientAccessGroupsQuery } from 'rtk/query/apis/clients';
import type { AccessGroup } from 'types/client.types';
import { isHubertAdminUser, isRecruiterUser, userHasPermission } from 'types/user.types';
import { useVisibilityLabels } from 'utils/visibilityLabels';

export const filterToggleClassName = clsx(
  'bg-primary-300 text-gray-50',
  'data-[state=on]:bg-primary data-[state=on]:text-gray-100',
  'hover:bg-primary-400 hover:text-gray-100',
  'h-5',
  'px-4',
);

/** The levels a client's own items have; Hubert library items belong to every client. Chosen when a client is picked. */
export const CLIENT_SHARING_LEVELS = ['client', 'local'];

export type TSharingLevelFilterValue = { visibility: string[]; accessGroupIds: string[] };

type Props = {
  value: TSharingLevelFilterValue;
  onChange: (value: TSharingLevelFilterValue) => void;
  /** The client Hubert staff have picked; their group picker offers that client's groups. */
  clientId?: string;
};

/**
 * Sharing-level toggles, plus a group picker while Selected groups is on. Hubert staff pick from the
 * chosen client's groups, super admins from all of their organization's, other recruiters from their own.
 */
const SharingLevelFilter = ({ value, onChange, clientId }: Props) => {
  const user = useUser();
  const { visibilityItems, visibilityItemName } = useVisibilityLabels();

  const groupsClientId = isHubertAdminUser(user)
    ? clientId
    : isRecruiterUser(user) && userHasPermission(user, TEMPLATE_PERMISSIONS.ManageClientLevel)
      ? user.client_id
      : '';
  const filteringOnGroups = value.visibility.includes('local');
  const { data: clientGroups } = useGetClientAccessGroupsQuery(
    { clientId: groupsClientId ?? '' },
    { skip: !groupsClientId || !filteringOnGroups },
  );
  const accessGroupOptions: AccessGroup[] | undefined = groupsClientId
    ? clientGroups?.accessGroups
    : isRecruiterUser(user)
      ? user.access_groups_permissions?.map((g) => ({ id: g.accessGroupId, name: g.accessGroupName }))
      : undefined;

  return (
    <>
      <ToggleGroup
        type="multiple"
        size={'sm'}
        variant={'outline'}
        aria-label={visibilityItemName}
        value={value.visibility}
        onValueChange={(visibility) =>
          onChange({ visibility, accessGroupIds: visibility.includes('local') ? value.accessGroupIds : [] })
        }
      >
        {visibilityItems.map((item) => (
          <ToggleGroupItem key={item.value} size={'sm'} className={filterToggleClassName} value={item.value}>
            {item.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      {filteringOnGroups && (
        <AccessGroupCombobox
          className="w-60"
          isMulti
          options={accessGroupOptions}
          value={value.accessGroupIds}
          onValueChange={(accessGroupIds) => onChange({ ...value, accessGroupIds: accessGroupIds as string[] })}
        />
      )}
    </>
  );
};

export default SharingLevelFilter;
